import type { Disposable } from "@/shared/contracts/lifecycle";
import { HoverChargeExecutionController } from "@/features/hoverIdle/core/hoverChargeExecutionController";

export const DEFAULT_OHOV_HOVER_CONFIRM_MS = 5000;

interface OHovHoverConfirmControllerOptions {
  readonly confirmMs?: number;
  readonly getConfirmDurationMs?: () => number;
  readonly isConfirmable: () => boolean;
  readonly onProgress: (progress: number) => void;
  readonly onComplete: () => void;
  readonly now?: () => number;
  readonly requestAnimationFrame?: (callback: FrameRequestCallback) => number;
  readonly cancelAnimationFrame?: (handle: number) => void;
}

export interface OHovHoverConfirmController extends Disposable {
  begin(): boolean;
  cancel(): void;
  syncConfirmable(): void;
  getProgress(): number;
}

export function createOHovHoverConfirmController(
  options: OHovHoverConfirmControllerOptions,
): OHovHoverConfirmController {
  const getConfirmDurationMs = (): number =>
    Math.max(
      0,
      options.getConfirmDurationMs?.() ??
        options.confirmMs ??
        DEFAULT_OHOV_HOVER_CONFIRM_MS,
    );
  const now = options.now ?? (() => performance.now());
  const requestFrame =
    options.requestAnimationFrame ??
    ((callback: FrameRequestCallback): number =>
      setTimeout(() => callback(now()), 16) as unknown as number);
  const cancelFrame =
    options.cancelAnimationFrame ??
    ((handle: number): void => {
      clearTimeout(handle as unknown as ReturnType<typeof setTimeout>);
    });

  const controller = new HoverChargeExecutionController({
    getDurationMs: getConfirmDurationMs,
    canExecute: options.isConfirmable,
    onProgress: options.onProgress,
    onExecute: options.onComplete,
    now,
    requestAnimationFrame: requestFrame,
    cancelAnimationFrame: cancelFrame,
  });

  return {
    begin(): boolean {
      return controller.setHovered(true);
    },
    cancel(): void {
      controller.setHovered(false);
    },
    syncConfirmable(): void {
      controller.syncCanExecute();
    },
    getProgress(): number {
      return controller.getProgress();
    },
    dispose(): void {
      controller.dispose();
    },
  };
}
